import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormGroup, FormControlLabel, Checkbox, Typography, IconButton,
    Box, Grid, Card, CardContent, CardActions
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GroupIcon from '@material-ui/icons/Group';
import { http } from '../../../fx-core';
import { AlertService } from '../../../fx-core';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: '600px',
            maxHeight: '90vh'
        }
    },
    scrollableList: {
        maxHeight: '400px',
        overflowY: 'auto',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        padding: theme.spacing(1)
    },
    cardGrid: {
        marginTop: theme.spacing(2)
    },
    card: {
        height: '100%',
        borderRadius: 10
    },
    title: {
        fontWeight: 600
    }
}));

interface CreateTrueFansGroupModalProps {
    open: boolean;
    stallId: number;
    stallName: string;
    followers: any[];
    onClose: () => void;
}

const CreateTrueFansGroupModal: React.FC<CreateTrueFansGroupModalProps> = ({
    open,
    stallId,
    stallName,
    followers,
    onClose
}) => {
    const classes = useStyles();
    const [tabValue, setTabValue] = useState(0);
    const [groupName, setGroupName] = useState('');
    const [groupDescription, setGroupDescription] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
    const [editGroupName, setEditGroupName] = useState('');
    const [editGroupDescription, setEditGroupDescription] = useState('');
    const [editGroupUsers, setEditGroupUsers] = useState<any[]>([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            fetchGroups();
        }
    }, [open]);

    const fetchGroups = async () => {
        try {
            const apiOptions = { url: `truefansgroup/stall/${stallId}`, data: null };
            const res = await http.get(apiOptions);
            if (res?.data?.success) {
                const mapped = (res.data.data || []).map((g: any) => ({
                    Id: g.Id ?? g.id,
                    GroupName: g.GroupName ?? g.groupName,
                    Description: g.Description ?? g.description,
                    MemberCount: g.MemberCount ?? g.memberCount ?? 0
                }));
                setGroups(mapped);
            }
        } catch (err) {
            console.error('Error fetching groups:', err);
        }
    };

    const handleUserSelect = (userId: number) => {
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            setErrorMessage('Group name is required');
            return;
        }

        if (selectedUserIds.length === 0) {
            setErrorMessage('Please select at least one user');
            return;
        }

        setLoading(true);
        try {
            const createGroupOptions = {
                url: `truefansgroup/create`,
                data: {
                    stallId,
                    groupName: groupName.trim(),
                    description: groupDescription.trim(),
                    createdBy: parseInt(localStorage.getItem('userId') || '0')
                }
            };

            const createRes = await http.post(createGroupOptions);
            const newGroupId = createRes.data.data?.Id ?? createRes.data.data?.id;

            if (newGroupId) {
                const addUsersOptions = {
                    url: `truefansgroup/${newGroupId}/add-users`,
                    data: { userIds: selectedUserIds }
                };
                await http.post(addUsersOptions);
                setSuccessMessage('TrueFans Group created successfully!');
                setTimeout(() => {
                    fetchGroups();
                    resetForm();
                    setTabValue(1);
                }, 1200);
            }
        } catch (err) {
            console.error('Error creating group:', err);
            setErrorMessage('Error creating group. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditGroup = async (groupId: number) => {
        try {
            const apiOptions = { url: `truefansgroup/${groupId}`, data: null };
            const res = await http.get(apiOptions);
            if (res.data.success) {
                const groupData = res.data.data;
                setEditingGroupId(groupId);
                setEditGroupName(groupData.groupName ?? groupData.GroupName);
                setEditGroupDescription(groupData.description ?? groupData.Description);
                const members = (groupData.Members ?? groupData.members ?? []).map((m: any) => ({
                    UserId: m.UserId ?? m.userId,
                    FirstName: m.FirstName ?? m.firstName,
                    LastName: m.LastName ?? m.lastName
                }));
                setEditGroupUsers(members);
            }
        } catch (err) {
            console.error('Error loading group:', err);
            setErrorMessage('Error loading group details.');
        }
    };

    const handleSaveEditGroup = async () => {
        if (!editGroupName.trim()) {
            setErrorMessage('Group name is required');
            return;
        }

        if (!editingGroupId) return;

        setLoading(true);
        try {
            const updateOptions = {
                url: `truefansgroup/${editingGroupId}`,
                data: { groupName: editGroupName.trim(), description: editGroupDescription.trim() }
            };
            await http.put(updateOptions);
            setSuccessMessage('Group updated successfully!');
            setTimeout(() => {
                fetchGroups();
                resetEditForm();
            }, 1200);
        } catch (err) {
            console.error('Error updating group:', err);
            setErrorMessage('Error updating group.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGroup = async (groupId: number) => {
        if (!window.confirm('Are you sure you want to delete this group?')) return;

        try {
            const deleteOptions = { url: `truefansgroup/${groupId}`, data: null };
            await http.delete(deleteOptions);
            setSuccessMessage('Group deleted successfully!');
            setTimeout(() => fetchGroups(), 1200);
        } catch (err) {
            console.error('Error deleting group:', err);
            setErrorMessage('Error deleting group.');
        }
    };

    const resetForm = () => {
        setGroupName('');
        setGroupDescription('');
        setSelectedUserIds([]);
        setErrorMessage('');
    };

    const resetEditForm = () => {
        setEditingGroupId(null);
        setEditGroupName('');
        setEditGroupDescription('');
        setEditGroupUsers([]);
        setErrorMessage('');
    };

    const handleClose = () => {
        resetForm();
        resetEditForm();
        setSuccessMessage('');
        setErrorMessage('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} className={classes.dialog} maxWidth="md" fullWidth>
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>TrueFans Group Management - {stallName}</span>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent dividers>
                {successMessage && AlertService.showSuccessMsg}
                {errorMessage && AlertService.showErrorMsg}

                {tabValue === 0 && (
                    <>
                        <TextField
                            label="Group Name"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            fullWidth
                            placeholder="e.g., VIP Customers"
                            variant="outlined"
                            size="small"
                            style={{ marginBottom: '12px' }}
                        />

                        <TextField
                            label="Description (Optional)"
                            value={groupDescription}
                            onChange={(e) => setGroupDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter group description"
                            variant="outlined"
                            size="small"
                            style={{ marginBottom: '12px' }}
                        />

                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '6px' }}>
                            Select Followers ({selectedUserIds.length} selected)
                        </Typography>
                        <div className={classes.scrollableList}>
                            <FormGroup>
                                {followers.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">No followers available</Typography>
                                ) : (
                                    followers.map((follower) => (
                                        <FormControlLabel
                                            key={follower.id}
                                            control={
                                                <Checkbox
                                                    checked={selectedUserIds.includes(follower.id)}
                                                    onChange={() => handleUserSelect(follower.id)}
                                                    color="primary"
                                                />
                                            }
                                            label={`${follower.firstName} ${follower.lastName || ''}`}
                                        />
                                    ))
                                )}
                            </FormGroup>
                        </div>
                    </>
                )}

                {tabValue === 1 && (
                    <div>
                        {editingGroupId ? (
                            <>
                                <Button
                                    onClick={() => resetEditForm()}
                                    variant="outlined"
                                    size="small"
                                    style={{ marginBottom: '16px' }}
                                >
                                    ← Back to Groups
                                </Button>
                                <TextField
                                    label="Group Name"
                                    value={editGroupName}
                                    onChange={(e) => setEditGroupName(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    style={{ marginBottom: '12px' }}
                                />
                                <TextField
                                    label="Description"
                                    value={editGroupDescription}
                                    onChange={(e) => setEditGroupDescription(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    size="small"
                                />
                            </>
                        ) : (
                            <Grid container spacing={2} className={classes.cardGrid}>
                                {groups.length === 0 ? (
                                    <Typography color="textSecondary" style={{ marginLeft: 16 }}>
                                        No TrueFans Groups created yet
                                    </Typography>
                                ) : (
                                    groups.map((group) => (
                                        <Grid item xs={12} sm={6} md={4} key={group.Id}>
                                            <Card className={classes.card} elevation={3}>
                                                <CardContent>
                                                    <Typography variant="h6" className={classes.title}>
                                                        {group.GroupName}
                                                    </Typography>
                                                    {group.Description && (
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            style={{ marginBottom: '8px' }}
                                                        >
                                                            {group.Description}
                                                        </Typography>
                                                    )}
                                                    <Box display="flex" alignItems="center">
                                                        <GroupIcon style={{ marginRight: '6px' }} />
                                                        <Typography variant="subtitle2">
                                                            {group.MemberCount} members
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                                <CardActions style={{ justifyContent: 'space-between' }}>
                                                    <Button
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => handleEditGroup(group.Id)}
                                                        color="primary"
                                                    >
                                                        View
                                                    </Button>
                                                    <IconButton
                                                        onClick={() => handleDeleteGroup(group.Id)}
                                                        color="secondary"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    ))
                                )}
                            </Grid>
                        )}
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="default">Close</Button>
                {tabValue === 0 && (
                    <Button
                        onClick={handleCreateGroup}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Create Group'}
                    </Button>
                )}
                {tabValue === 1 && editingGroupId && (
                    <Button
                        onClick={handleSaveEditGroup}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateTrueFansGroupModal;